import * as THREE from 'three';
import type { SurfaceProperties } from './SurfaceProperties';
import type { LaunchConditions } from './LaunchCalibration';

export interface LieAdjustedLaunch extends LaunchConditions {
  readonly spinMultiplier: number;
  readonly sidespinMultiplier: number;
}

/** Applies data-driven surface lie multipliers to launch output. */
export function applyLieEffects(
  launch: LaunchConditions,
  surface: SurfaceProperties,
  aimRadians: number,
): LieAdjustedLaunch {
  const lie = surface.lieEffect;
  const speed = launch.launchSpeedMetersPerSecond * lie.distanceMultiplier;
  const horizontal = new THREE.Vector3(launch.velocity.x, 0, launch.velocity.z);
  const horizontalSpeed = horizontal.length();
  const directionRadians =
    (horizontalSpeed > 0.001 ? Math.atan2(horizontal.x, horizontal.z) : aimRadians) +
    THREE.MathUtils.degToRad(lie.fixedDirectionalBiasDegrees);
  const adjustedAngleDegrees = THREE.MathUtils.clamp(
    launch.launchAngleDegrees + lie.launchAngleDeltaDegrees,
    0.5,
    60,
  );
  const launchAngle = THREE.MathUtils.degToRad(adjustedAngleDegrees);
  const direction = new THREE.Vector3(Math.sin(directionRadians), 0, Math.cos(directionRadians));
  const velocity = direction.multiplyScalar(Math.cos(launchAngle) * speed);
  velocity.y = Math.sin(launchAngle) * speed;

  return {
    ...launch,
    velocity,
    launchSpeedMetersPerSecond: speed,
    launchAngleDegrees: adjustedAngleDegrees,
    backspinRpm: launch.backspinRpm * lie.spinMultiplier,
    expectedCarryYards: launch.expectedCarryYards * lie.distanceMultiplier,
    rolloutResistanceMultiplier:
      launch.rolloutResistanceMultiplier * lie.rolloutResistanceMultiplier,
    bounceRestitutionMultiplier:
      launch.bounceRestitutionMultiplier * lie.bounceRestitutionMultiplier,
    spinMultiplier: lie.spinMultiplier,
    sidespinMultiplier: lie.sidespinMultiplier,
  };
}
